import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

import { BehaviorSubject } from 'rxjs';

import { PreloaderClassNames } from '@hdms/shared/common-types';

export enum PreloadType {
  REQUEST = 'request',
  NAVIGATION = 'navigation',
  MANUAL = 'manual'
}

@Injectable({
  providedIn: 'root'
})
export class PreloaderService {
  // make instance available in UsePagePreloader decorator without using DI
  public static instance: PreloaderService;

  public loadingStatus: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly requestsToTrack = new Set();
  private readonly loadingProgress = {
    [PreloadType.NAVIGATION]: false,
    [PreloadType.REQUEST]: false,
    [PreloadType.MANUAL]: false
  };

  private blockStopLoading = false;

  constructor(@Inject(DOCUMENT) private readonly doc: Document, router: Router) {
    PreloaderService.instance = this;

    router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.startLoading(PreloadType.NAVIGATION);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.stopLoading(PreloadType.NAVIGATION);
      }
    });
  }

  get loading(): boolean {
    return this.loadingStatus.value;
  }

  set loading(value) {
    if (value !== this.loadingStatus.value) {
      this.loadingStatus.next(value);
    }
  }

  startLoading(preloadType: PreloadType = PreloadType.MANUAL): void {
    this.loadingProgress[preloadType] = true;

    this.loading = true;
  }

  stopLoading(preloadType: PreloadType = PreloadType.MANUAL): void {
    if (this.blockStopLoading) {
      return;
    }

    this.loadingProgress[preloadType] = false;

    if (
      !this.loadingProgress[PreloadType.NAVIGATION] &&
      !this.loadingProgress[PreloadType.REQUEST] &&
      !this.loadingProgress[PreloadType.MANUAL]
    ) {
      this.loading = false;
    }
  }

  registerRequest(identifier: string): void {
    identifier && this.requestsToTrack.add(identifier);
  }

  shouldTrackRequest(identifier: string): boolean {
    return Array.from(this.requestsToTrack).includes(identifier);
  }

  /**
   * Required to activate page-preloader on app init before PagePreloaderDirective would become initialized.
   * TODO: consider moving PagePreloaderDirective logic into this service in order to solve this limitation.
   */
  setInitialLoading(): void {
    this.blockStopLoading = true;

    this.doc.body.classList.add(PreloaderClassNames.BODY_ACTIVE);

    const preloader = this.doc.querySelector<HTMLElement>(`.${PreloaderClassNames.PAGE_PRELOADER}`);
    if (preloader) {
      preloader.classList.add(PreloaderClassNames.ACTIVE);
      // force a reflow
      void preloader.offsetHeight;
      preloader.classList.add(PreloaderClassNames.SHOW);
    }
  }
}
